import {
  isQueryBuilderSubquery,
  Operator,
  QueryBuilderRow,
  QueryBuilderRule,
  QueryBuilderSubquery,
  ReferenceFilterIds,
} from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import { formatDateISO, parseDate } from '@ardoq/date-time';
import {
  CREATED_BY,
  LAST_MODIFIED_BY,
  LAST_UPDATED,
  ReferenceTypeFilterIds,
} from '@ardoq/query-builder';
import { AGGridPredefinedFilterOperator } from '../types';
import {
  hasValueSelectOption,
  noValueSelectOption,
  predefinedSelectOptions,
} from './agGridFilterOperations';
import { FilterEntry, FilterModel, SetFilterEntry } from './types';

export const mapSubqueryToFilterModel = (
  subquery: QueryBuilderSubquery | null
): FilterModel => {
  if (!subquery) {
    return {};
  }
  return mapQueryRowToFilterEntry(subquery);
};

const mapQueryRowToFilterEntry = (
  row: QueryBuilderRow
): Record<string, FilterEntry> => {
  if (isQueryBuilderSubquery(row)) {
    return row.rules.reduce((prev, current) => {
      return mergeFilterEntries(prev, mapQueryRowToFilterEntry(current));
    }, {});
  }
  return mapQueryBuilderRuleToFilterEntry(row);
};

const mergeFilterEntries = (
  acc: Record<string, FilterEntry>,
  current: Record<string, FilterEntry>
): Record<string, FilterEntry> => {
  const result = { ...acc };
  Object.entries(current).forEach(([key, value]) => {
    if (
      value.filterType === 'set' &&
      result[key] &&
      result[key].filterType === 'set'
    ) {
      // we need to merge set filters form multiple rules to one filter model
      const accSetFilter = result[key] as SetFilterEntry;
      const currentSetFilter = value as SetFilterEntry;
      result[key] = {
        ...result[key],
        filterType: 'set',
        values: [
          ...new Set([
            ...(accSetFilter.values ?? []),
            ...(currentSetFilter.values ?? []),
          ]),
        ],
      };
    } else {
      result[key] = { ...value };
    }
  });
  return result;
};

export const mapReferenceFilterToValue = (rule: QueryBuilderRule): string => {
  if (
    rule.field === ReferenceTypeFilterIds.HAS_OUTGOING_REFERENCE_TYPE ||
    rule.field === ReferenceTypeFilterIds.HAS_INCOMING_REFERENCE_TYPE
  ) {
    return predefinedSelectOptions.hasReferences;
  }

  if (
    rule.field ===
      ReferenceTypeFilterIds.DOES_NOT_HAVE_OUTGOING_REFERENCE_TYPE ||
    rule.field === ReferenceTypeFilterIds.DOES_NOT_HAVE_INCOMING_REFERENCE_TYPE
  ) {
    return predefinedSelectOptions.noReferences;
  }

  return rule.value as string;
};

const mapValueForSetFilterToAgGridFormat = (
  rule: QueryBuilderRule
): string | number | null => {
  if (rule.operator === Operator.IS_EMPTY) {
    return noValueSelectOption;
  }
  if (rule.operator === Operator.IS_NOT_EMPTY) {
    return hasValueSelectOption;
  }
  if (rule.type === 'boolean') {
    return mapBooleanRuleValueToFilterEntry(rule);
  }
  return rule.value as string | number | null;
};

const mapBooleanRuleValueToFilterEntry = (
  rule: QueryBuilderRule
): string | null => {
  if (rule.value === null) {
    return null;
  }
  return rule.value ? 'true' : 'false';
};

const mapRuleWithSelectInputToSetFilterModel = (
  rule: QueryBuilderRule
): Record<string, FilterEntry> => {
  const filterEntry: SetFilterEntry = {
    filterType: 'set',
    values: [mapValueForSetFilterToAgGridFormat(rule)],
  };
  return {
    [mapArdoqFieldIdToColumnId(rule.field)]: filterEntry,
  };
};

const mapReferenceTypeFilterSetFilterModel = (
  rule: QueryBuilderRule
): Record<string, FilterEntry> => {
  const columnId = mapReferenceTypeFilterToColumnId(rule);
  if (columnId === null) return {};
  const filterEntry: SetFilterEntry = {
    filterType: 'set',
    values: [mapReferenceFilterToValue(rule)],
  };
  return {
    [columnId]: filterEntry,
  };
};

const mapTypeNameToSetFilterModel = (
  rule: QueryBuilderRule
): Record<string, FilterEntry> => {
  return mapRuleWithSelectInputToSetFilterModel(rule);
};

const mapParentRuleIntoSetFilterModel = (
  rule: QueryBuilderRule
): Record<string, FilterEntry> => {
  return mapRuleWithSelectInputToSetFilterModel(rule);
};

const mapDateRuleIntoDateFilterModel = (
  rule: QueryBuilderRule
): Record<string, FilterEntry> => {
  const key = dateRangeOperations.fieldNameIsPartOfDateRangeField(rule.field)
    ? dateRangeOperations.extractDateRangeFieldName(rule.field)
    : mapArdoqFieldIdToColumnId(rule.field);

  if (
    rule.operator === Operator.IS_EMPTY ||
    rule.operator === Operator.IS_NOT_EMPTY
  ) {
    return {
      [key]: {
        filterType: 'date',
        dateFrom: null,
        dateTo: null,
        type: mapOperatorToAGGridFilter(rule.operator),
      },
    };
  }
  if (rule.operator === Operator.BETWEEN) {
    return {
      [key]: {
        filterType: 'date',
        dateFrom: (rule.value as null | string[])?.[0]
          ? formatDateToAgGridFormat((rule.value as string[])[0])
          : null,
        dateTo: (rule.value as null | string[])?.[1]
          ? formatDateToAgGridFormat((rule.value as string[])[1])
          : null,
        type: mapOperatorToAGGridFilter(rule.operator),
      },
    };
  }
  return {
    [key]: {
      filterType: 'date',
      dateFrom: formatDateToAgGridFormat(rule.value as string),
      dateTo: null,
      type: mapOperatorToAGGridFilter(rule.operator),
    },
  };
};

const formatDateToAgGridFormat = (value: string) => {
  return `${formatDateISO(parseDate(value))} 00:00:00`;
};

const mapTextOrNumberInputRuleToFilterModel = (
  rule: QueryBuilderRule
): Record<string, FilterEntry> => {
  const filterEntry = {
    filterType: rule.input === 'number' ? 'number' : 'text',
    type: mapOperatorToAGGridFilter(rule.operator),
    filter: rule.value as string | number | boolean | null,
  } as const;
  return { [mapArdoqFieldIdToColumnId(rule.field)]: filterEntry };
};

// Note: Inventory doesn't produce ReferenceFilterIds.HAS_NO_REFERENCE rule now
export const isReferenceRule = (rule: QueryBuilderRule): boolean =>
  rule.field === ReferenceFilterIds.HAS_REFERENCE ||
  rule.field === ReferenceTypeFilterIds.HAS_INCOMING_REFERENCE_TYPE ||
  rule.field === ReferenceTypeFilterIds.DOES_NOT_HAVE_INCOMING_REFERENCE_TYPE ||
  rule.field === ReferenceTypeFilterIds.HAS_OUTGOING_REFERENCE_TYPE ||
  rule.field === ReferenceTypeFilterIds.DOES_NOT_HAVE_OUTGOING_REFERENCE_TYPE;

const mapQueryBuilderRuleToFilterEntry = (
  rule: QueryBuilderRule
): Record<string, FilterEntry> => {
  if (rule.field === 'typeName') {
    return mapTypeNameToSetFilterModel(rule);
  }
  if (rule.field === 'parent') {
    return mapParentRuleIntoSetFilterModel(rule);
  }
  if (rule.field === 'lucidchartDocuments') {
    return mapRuleWithSelectInputToSetFilterModel(rule);
  }
  if (isReferenceRule(rule)) {
    return mapReferenceTypeFilterSetFilterModel(rule);
  }
  if (rule.input === 'select') {
    return mapRuleWithSelectInputToSetFilterModel(rule);
  }

  if (rule.input === 'date') {
    return mapDateRuleIntoDateFilterModel(rule);
  }

  return mapTextOrNumberInputRuleToFilterModel(rule);
};

const removeSpaces = (value: string): string => {
  return (value as string).replaceAll(' ', '');
};

export const mapReferenceTypeFilterToColumnId = (
  rule: QueryBuilderRule
): string | null => {
  if (rule.field === ReferenceFilterIds.HAS_REFERENCE) {
    const referenceTypeName =
      'referenceTypeName' in rule && typeof rule.referenceTypeName === 'string'
        ? rule.referenceTypeName
        : null;
    if (!referenceTypeName) return null;
    const direction = rule.operator === Operator.TO ? 'outgoing' : 'incoming';
    return `${removeSpaces(referenceTypeName)}--${direction}`;
  }

  const referenceTypeNameWithoutSpaces = removeSpaces(
    (rule.value ?? '') as string
  );
  const direction =
    rule.field ===
      ReferenceTypeFilterIds.DOES_NOT_HAVE_OUTGOING_REFERENCE_TYPE ||
    rule.field === ReferenceTypeFilterIds.HAS_OUTGOING_REFERENCE_TYPE
      ? 'outgoing'
      : 'incoming';

  return `${referenceTypeNameWithoutSpaces}--${direction}`;
};

const mapArdoqFieldIdToColumnId = (fieldId: string): string => {
  if (fieldId === 'typeName') {
    return 'type';
  }
  if (fieldId === LAST_MODIFIED_BY.id) {
    return 'lastModifiedBy';
  }
  if (fieldId === LAST_UPDATED.id) {
    return 'lastUpdated';
  }
  if (fieldId === CREATED_BY.id) {
    return 'createdBy';
  }
  return fieldId;
};

const mapOperatorToAGGridFilter = (
  operator: Operator
): AGGridPredefinedFilterOperator => {
  switch (operator) {
    case Operator.EQUAL:
      return 'equals';
    case Operator.NOT_EQUAL:
      return 'notEqual';
    case Operator.CONTAINS_SUBSTRING:
      return 'contains';
    case Operator.NOT_CONTAINS_SUBSTRING:
      return 'notContains';
    case Operator.LESS:
      return 'lessThan';
    case Operator.LESS_OR_EQUAL:
      return 'lessThanOrEqual';
    case Operator.GREATER:
      return 'greaterThan';
    case Operator.GREATER_OR_EQUAL:
      return 'greaterThanOrEqual';
    case Operator.BETWEEN:
      return 'inRange';
    case Operator.IS_EMPTY:
      return 'blank';
    case Operator.IS_NOT_EMPTY:
      return 'notBlank';
    default:
      throw new Error(`Unsupported operator: ${operator}`);
  }
};
